import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { CalcBreakdown } from "../components/CalcBreakdown";
import { LimitationCard } from "../components/LimitationCard";
import { formatDate } from "../lib/format";
import {
  canManageLegal,
  fetchObligationLegal,
  setAccrualDue,
  setManualStatus,
  type ObligationLegal,
} from "../lib/legal";
import type { Role } from "../lib/roles";
import { ClaimsPanel, ReasonForm } from "./ClaimsPanel";

/**
 * Правовая карточка обязательства (E9): расчёт санкций, исковая давность, претензии и правовые
 * решения юриста («Спор» / «Снято», дата начала начисления).
 *
 * Дата расчёта — управляемый параметр (по умолчанию «сегодня» организации): юристу нужно считать
 * и на дату претензии, и на дату судебного заседания. Считает всё сервер — здесь только показ.
 */

const btnPrimary =
  "rounded-[var(--radius-m)] bg-accent px-3 py-1.5 text-[13px] font-bold text-surface disabled:opacity-50";
const btnSecondary =
  "rounded-[var(--radius-m)] border border-border px-3 py-1.5 text-[13px] font-bold text-ink hover:bg-bg disabled:opacity-50";
const inputCls =
  "w-full rounded-[var(--radius-m)] border border-border bg-surface px-2.5 py-1.5 text-[14px]";
const labelCls = "mb-1 block text-[13px] text-muted";

type Pending = "dispute" | "cancelled" | "release" | "accrual" | null;

/**
 * Поле даты правового решения (D-6): обязательно вместе с ручным статусом.
 *
 * Поле НЕ предзаполняется «сегодня»: календарный день считается по поясу организации (Омск, Р-16),
 * а браузер юриста может стоять в другом поясе — подставленная им дата уехала бы на сутки. Проверку
 * «не в будущем» делает сервер и объясняет отказ текстом.
 */
function DecisionDateField({
  id,
  label,
  hint,
  value,
  onChange,
}: {
  id: string;
  label: string;
  hint: string;
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <div className="mt-2">
      <label className={labelCls} htmlFor={id}>
        {label}
      </label>
      {/* Без `required`: поле стоит ВНЕ формы основания, и браузерная проверка тут не сработала бы
          вовсе — пустую дату ловит сама панель перед отправкой (applyStatus). */}
      <input
        id={id}
        type="date"
        className={inputCls}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <p className="mt-1 text-[12px] text-muted">{hint}</p>
    </div>
  );
}

export function ObligationLegalPanel({
  obligationId,
  roles,
  onObligationChanged,
}: {
  obligationId: string;
  roles: readonly Role[];
  onObligationChanged?: () => void;
}) {
  const [on, setOn] = useState("");
  const [legal, setLegal] = useState<ObligationLegal | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending>(null);
  const [caseNo, setCaseNo] = useState("");
  const [accrualDate, setAccrualDate] = useState("");
  /** Дата правового решения «Спор»/«Снято» (D-6). Пустая по умолчанию: подставлять «сегодня»
   *  браузера нельзя — его день может не совпасть с днём организации (Омск, Р-16). */
  const [statusDate, setStatusDate] = useState("");

  const canManage = canManageLegal(roles);

  const load = useCallback(async () => {
    const res = await fetchObligationLegal(obligationId, on === "" ? undefined : on);
    if (res.ok) {
      setLegal(res.value);
      setError(null);
      return;
    }
    setError(res.message);
  }, [obligationId, on]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error !== null && legal === null) {
    return (
      <p role="alert" className="text-[13px] text-overdue-fg">
        {error}
      </p>
    );
  }
  if (legal === null) {
    return (
      <p role="status" className="text-[13px] text-muted">
        Загрузка расчёта…
      </p>
    );
  }

  const manual = legal.obligation.manualStatus;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className={labelCls} htmlFor={`calc-on-${obligationId}`}>
            Дата расчёта
          </label>
          <input
            id={`calc-on-${obligationId}`}
            type="date"
            className={inputCls}
            value={on}
            onChange={(e) => setOn(e.target.value)}
          />
        </div>
        {on !== "" && (
          <button type="button" className={btnSecondary} onClick={() => setOn("")}>
            На сегодня
          </button>
        )}
        {/* Печатная форма (E9a). Ссылка несёт ВЫБРАННУЮ дату расчёта: поле пустое — сервер возьмёт
            «сегодня» по поясу организации (Р-16), выбрана дата — печатается ровно то, что на
            экране. Печать доступна всем, кто видит расчёт: отдельного права нет. */}
        <Link
          className={btnSecondary}
          to={`/print/calc/obligation/${obligationId}${on === "" ? "" : `?on=${encodeURIComponent(on)}`}`}
        >
          Печатная форма расчёта
        </Link>
      </div>

      {error !== null && (
        <p role="alert" className="text-[13px] text-overdue-fg">
          {error}
        </p>
      )}

      {manual === "dispute" && (
        <p className="rounded-[var(--radius-m)] bg-dispute-bg px-2.5 py-1.5 text-[13px] text-dispute-fg">
          Обязательство в статусе «Спор»
          {legal.obligation.disputeCaseNo !== null && `, дело № ${legal.obligation.disputeCaseNo}`}
          {legal.obligation.manualStatusDate !== null &&
            ` (решение от ${formatDate(legal.obligation.manualStatusDate)})`}
          . Начисление санкций продолжается.
          {legal.obligation.manualStatusReason !== null &&
            ` Основание: ${legal.obligation.manualStatusReason}.`}
        </p>
      )}
      {/* Честная формулировка вернулась вместе с датой снятия (D-6): до неё интерфейс не мог
          обещать прекращение начисления, потому что начисление шло дальше. */}
      {manual === "cancelled" && (
        <p className="rounded-[var(--radius-m)] bg-waiting-bg px-2.5 py-1.5 text-[13px] text-waiting-fg">
          Обязательство снято с контроля
          {legal.obligation.manualStatusDate !== null &&
            `: начисление неустойки и процентов по ст. 395 ГК РФ прекращено с ${formatDate(
              legal.obligation.manualStatusDate,
            )} (этот день в расчёт входит)`}
          .
          {legal.obligation.manualStatusReason !== null &&
            ` Основание: ${legal.obligation.manualStatusReason}.`}{" "}
          В сводные суммы раздела «Претензии и риски» обязательство не входит.
        </p>
      )}

      <CalcBreakdown calc={legal.calc} />

      {canManage && (
        <div className="rounded-[var(--radius-l)] border border-border bg-surface p-3">
          <h3 className="text-[15px] font-bold text-ink">Решения юриста</h3>
          <div className="mt-2 flex flex-wrap gap-2">
            {manual !== "dispute" && (
              <button type="button" className={btnSecondary} onClick={() => toggle("dispute")}>
                Перевести в спор
              </button>
            )}
            {manual !== "cancelled" && (
              <button type="button" className={btnSecondary} onClick={() => toggle("cancelled")}>
                Снять с контроля
              </button>
            )}
            {manual !== null && (
              <button type="button" className={btnSecondary} onClick={() => toggle("release")}>
                Вернуть на контроль
              </button>
            )}
            <button type="button" className={btnSecondary} onClick={() => toggle("accrual")}>
              {legal.calc.accrualOverride === null
                ? "Задать дату начисления"
                : "Изменить дату начисления"}
            </button>
            {legal.calc.accrualOverride !== null && (
              <button
                type="button"
                className={btnSecondary}
                onClick={() => void applyAccrual(null, undefined)}
              >
                Вернуть расчётный срок
              </button>
            )}
          </div>

          {pending === "dispute" && (
            <div>
              <label className={labelCls} htmlFor="dispute-case">
                № судебного дела (необязательно — дело может быть ещё не подано)
              </label>
              <input
                id="dispute-case"
                className={inputCls}
                value={caseNo}
                onChange={(e) => setCaseNo(e.target.value)}
              />
              <DecisionDateField
                id="dispute-date"
                label="Дата решения о переводе в спор"
                hint="Начисление санкций в споре продолжается (ТЗ 4.2.5) — дата нужна для карточки и печатной формы. Будущим числом дату поставить нельзя."
                value={statusDate}
                onChange={setStatusDate}
              />
              <ReasonForm
                label="Основание перевода в спор"
                submitLabel="Перевести в спор"
                onCancel={() => setPending(null)}
                onSubmit={(reason) => applyStatus("dispute", reason)}
              />
            </div>
          )}

          {pending === "cancelled" && (
            <div>
              <DecisionDateField
                id="cancelled-date"
                label="Дата снятия с контроля"
                // «Дата документа, а не дата внесения» — ответ юриста заказчика 17.09.2026 по
                // D-6: акт подписан 10-го, внесён 15-го — начисление прекращается 10-м. До
                // ответа это было нашим прочтением (Р-31) и в подсказке не называлось.
                hint="Этой датой ВКЛЮЧИТЕЛЬНО прекращается начисление неустойки и процентов по ст. 395 ГК РФ. Ставьте дату документа-основания, а не дату внесения в систему. Будущим числом дату поставить нельзя."
                value={statusDate}
                onChange={setStatusDate}
              />
              <ReasonForm
                label="Основание снятия с контроля"
                // Реквизиты документа-основания просит САМА подсказка (D-6, ответ юриста
                // 17.09.2026; решение владельца 28.09.2026 — вариант А: подсказка и справка,
                // без отдельных полей вида/номера/даты). Печатная форма расчёта выводит
                // основание строкой, поэтому «по какому документу сняли» читается из неё.
                hint="Вид, номер и дата документа-основания, например: Соглашение о расторжении № 12 от 10.08.2026. Расторжение, зачёт, новация и т. п. Реквизиты печатаются в расчёте."
                submitLabel="Снять с контроля"
                onCancel={() => setPending(null)}
                onSubmit={(reason) => applyStatus("cancelled", reason)}
              />
            </div>
          )}

          {pending === "release" && (
            // Без поля основания: при снятии ручного статуса основание обязано быть пустым
            // (CHECK `obligation_manual_status_reason_chk`), поэтому просить его — значит
            // потерять введённый текст молча. Само действие остаётся в журнале аудита.
            <div className="mt-2 rounded-[var(--radius-m)] border border-border-soft p-3">
              <p className="text-[13px] text-ink">
                Вернуть обязательство под обычный контроль? Ручной статус будет снят, № судебного
                дела и основание — очищены. Действие останется в журнале аудита.
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  className={btnPrimary}
                  onClick={() => void applyStatus(null, undefined)}
                >
                  Вернуть на контроль
                </button>
                <button type="button" className={btnSecondary} onClick={() => setPending(null)}>
                  Отмена
                </button>
              </div>
            </div>
          )}

          {pending === "accrual" && (
            <div>
              <label className={labelCls} htmlFor="accrual-date">
                Считать неустойку с даты
              </label>
              <input
                id="accrual-date"
                type="date"
                className={inputCls}
                value={accrualDate}
                onChange={(e) => setAccrualDate(e.target.value)}
              />
              <p className="mt-1 text-[12px] text-muted">
                Срок по данным системы — {formatDate(legal.calc.dueComputed)}. Обе даты и основание
                останутся видны в карточке и в печатной форме.
              </p>
              <ReasonForm
                label="Основание (мораторий, рассрочка, иное)"
                submitLabel="Задать дату"
                onCancel={() => setPending(null)}
                onSubmit={(reason) => applyAccrual(accrualDate, reason)}
              />
            </div>
          )}
        </div>
      )}

      <LimitationCard limitation={legal.limitation} />

      <ClaimsPanel legal={legal} canManage={canManage} onChanged={() => void load()} />
    </div>
  );

  function toggle(next: Exclude<Pending, null>): void {
    // Дата из закрытой формы не должна «переезжать» в другую: решение о споре и решение о снятии
    // с контроля — разные события разных дат.
    setStatusDate("");
    setPending(pending === next ? null : next);
  }

  async function applyStatus(
    status: "dispute" | "cancelled" | null,
    reason: string | undefined,
  ): Promise<boolean> {
    // Дата решения обязательна вместе со статусом (D-6). Проверяем до отправки: иначе юрист
    // отправил бы форму и получил серверный отказ, потеряв уже введённое основание.
    if (status !== null && statusDate === "") {
      setError(
        status === "cancelled"
          ? "Укажите дату снятия с контроля: этой датой прекращается начисление санкций."
          : "Укажите дату решения о переводе обязательства в спор.",
      );
      return false;
    }
    const res = await setManualStatus(obligationId, {
      status,
      ...(status === "dispute" && caseNo.trim() !== "" ? { caseNo: caseNo.trim() } : {}),
      ...(reason === undefined ? {} : { reason }),
      // Дата уходит только со статусом: при возврате на контроль сервер стирает её вместе с
      // основанием и № дела (CHECK-парность в БД).
      ...(status !== null && statusDate !== "" ? { date: statusDate } : {}),
    });
    if (!res.ok) {
      setError(res.message);
      return false;
    }
    setError(null);
    setPending(null);
    setCaseNo("");
    setStatusDate("");
    await load();
    onObligationChanged?.();
    return true;
  }

  async function applyAccrual(date: string | null, reason: string | undefined): Promise<boolean> {
    const res = await setAccrualDue(obligationId, {
      date: date === "" ? null : date,
      ...(reason === undefined ? {} : { reason }),
    });
    if (!res.ok) {
      setError(res.message);
      return false;
    }
    setError(null);
    setPending(null);
    setAccrualDate("");
    await load();
    onObligationChanged?.();
    return true;
  }
}
